import { Card, Grid, Box, Divider } from "@mui/material";
import { sentenceCase } from "change-case";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDatePicker from "components/MDDatePicker";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import CustomSelect from "components/Shared/CustomSelect/CustomSelect";
import dayjs from "dayjs";
import { GenerateReportMutationVariables, ReportType } from "generated/graphql";
import useGenerateReport from "hooks/organization/reports/useGenerateReport";
import { kebabCase } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

const Generate = () => {
  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
  } = useForm();

  const [generateReport, { data }] = useGenerateReport();
  const [requestData, setRequestData] = useState<GenerateReportMutationVariables | null>(null);

  const downloadFileRef = useRef(null);

  useEffect(() => {
    if (data?.generateReport) {
      downloadFileRef.current.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${encodeURI(
        data.generateReport
      )}`;
      downloadFileRef.current.download = `bitumio-${kebabCase(requestData.type)}-report.xlsx`;
      downloadFileRef.current.click();
    }
  }, [data]);

  const tzoffset = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
  let today = new Date(Date.now() - tzoffset).toISOString().slice(0, 10);
  today = `${today} 00:00:00`;

  const [date, setDate] = useState({ startDate: today, endDate: today });

  const changeDate = async (selectedDate: Array<Date>, type) => {
    const formattedDate = new Date(selectedDate[0].getTime() - tzoffset).toISOString().slice(0, 10);
    setDate((date) => ({ ...date, [type]: `${formattedDate} 00:00:00` }));
  };

  const dataValues = Object.values(ReportType).map((type) => ({
    label: sentenceCase(type),
    value: type,
  }));

  const onSubmit = (data) => {
    const variables = { ...data, ...date };
    setRequestData(variables);
    generateReport({ variables });
  };

  const excludeFromDateRangeLimit = [ReportType.CONTACT_DETAILS];

  return (
    <Box
      sx={{
        "& .MuiOutlinedInput-root": {
          width: "100% !important",
          padding: "5px !important",
          height: "56px",
          backgroundColor: "#f8f9fa",
          borderRadius: 2,
          "&:hover": {
            backgroundColor: "#f1f3f4",
          },
          "&.Mui-focused": {
            backgroundColor: "#fff",
          },
        },
        "& .MuiFormControl-root": {
          width: "100% !important",
        },
      }}
    >
      <Card elevation={3} sx={{ borderRadius: 3 }}>
        {/* Header Section */}
        <MDBox p={4} pb={2}>
          <MDTypography variant="h4" fontWeight="medium" color="dark" mb={1}>
            Download Reports
          </MDTypography>

          <Divider />
        </MDBox>

        {/* Form Section */}
        <MDBox p={4} component="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} alignItems="flex-end" justifyContent="space-between">
            {/* Report Type Selection */}
            <Grid item xs={12} md={4} lg={4}>
              <MDBox mb={1}>
                <MDTypography variant="body2" fontWeight="medium" color="dark">
                  Type
                </MDTypography>
              </MDBox>
              <Box>
                <CustomSelect
                  label="Select report type"
                  control={control}
                  fullWidth
                  selectVariant="outlined"
                  name="type"
                  data={dataValues}
                  {...register("type", {
                    required: true,
                    validate: (v) => {
                      if (excludeFromDateRangeLimit.includes(v)) {
                        return true;
                      }
                      const endDate = dayjs(date.endDate);
                      return endDate.diff(date.startDate, "month") <= 5;
                    },
                  })}
                />
              </Box>
            </Grid>

            {/* Start Date */}
            <Grid item xs={4} md={4} lg={4}>
              <MDBox mb={1}>
                <MDTypography variant="body2" fontWeight="medium" color="dark">
                  Start Date
                </MDTypography>
              </MDBox>
              <MDDatePicker
                defaultValue={today}
                onChange={(val) => changeDate(val, "startDate")}
                fullWidth
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={4} md={4} lg={4}>
              <MDBox mb={1}>
                <MDTypography variant="body2" fontWeight="medium" color="dark">
                  End Date
                </MDTypography>
              </MDBox>
              <MDDatePicker
                defaultValue={today}
                onChange={(val) => changeDate(val, "endDate")}
                fullWidth
              />
            </Grid>

            {/* Error Message */}
            {errors.type && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#ffebee",
                    borderRadius: 2,
                    border: "1px solid #ffcdd2",
                  }}
                >
                  <MDTypography color="error" variant="body2">
                    {`Report too large. Please select a smaller date range (< 6 months).`}
                  </MDTypography>
                </Box>
              </Grid>
            )}

            {/* Submit Button */}
            <Grid item xs={12}>
              <MDBox mt={2}>
                <a ref={downloadFileRef} className="d-none" />
                <MDButton
                  variant="gradient"
                  color="success"
                  type="submit"
                  size="large"
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "uppercase",
                    fontSize: "0.875rem",
                    fontWeight: "bold",
                    letterSpacing: "0.5px",
                  }}
                >
                  Save
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </Card>
    </Box>
  );
};

export default Generate;
